/**
 * DeleteConfirmModal — W4.8
 *
 * Generisches Bestaetigungs-Modal fuer Contact + Share Loeschung.
 */
import { useTranslation } from 'react-i18next'

type Props = {
  title: string
  message: string
  isOpen: boolean
  isPending: boolean
  onConfirm: () => void
  onClose: () => void
  /**
   * R6/MP: i18n-Namespace fuer die generischen Buttons (Abbrechen/Loeschen).
   * Default 'contacts' — Bestandsaufrufe bleiben unveraendert. Der Namespace
   * muss deleteModal.closeAriaLabel/cancelBtn/deleteBtn/deletePending kennen.
   */
  namespace?: string
  /** R6/MP: zusaetzlicher Warnhinweis unter der Nachricht (z. B. Reimport-Hinweis). */
  extraWarning?: string
}

export function DeleteConfirmModal({
  title,
  message,
  isOpen,
  isPending,
  onConfirm,
  onClose,
  namespace = 'contacts',
  extraWarning,
}: Props) {
  const { t } = useTranslation(namespace)
  if (!isOpen) return null

  return (
    <div
      className="modal-backdrop"
      role="dialog"
      aria-modal="true"
      aria-labelledby="delete-modal-title"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose()
      }}
      data-testid="delete-confirm-modal"
    >
      <div className="modal">
        <div className="modal-header">
          <h2 id="delete-modal-title">{title}</h2>
          <button
            type="button"
            className="btn btn-ghost btn-sm"
            aria-label={t('deleteModal.closeAriaLabel')}
            onClick={onClose}
          >
            &times;
          </button>
        </div>

        <div className="modal-body">
          <p>{message}</p>
          {extraWarning && (
            <p className="muted" data-testid="delete-confirm-warning" style={{ marginTop: 8 }}>
              {extraWarning}
            </p>
          )}
        </div>

        <div className="modal-footer">
          <button
            type="button"
            className="btn btn-ghost"
            onClick={onClose}
            disabled={isPending}
          >
            {t('deleteModal.cancelBtn')}
          </button>
          <button
            type="button"
            className="btn btn-danger"
            onClick={onConfirm}
            disabled={isPending}
            data-testid="delete-confirm-btn"
          >
            {isPending ? t('deleteModal.deletePending') : t('deleteModal.deleteBtn')}
          </button>
        </div>
      </div>
    </div>
  )
}
